import { NoTzDate } from "../DateUtils"

export interface ILastEstimateResponse {
    estimationId?: number,
    apptTypeDesc?: string,
    providerName?: string,
    payerName?: string,
    isEstimateAccepted: boolean,
    allowedAmount?: number,
    patientAmountInNetwork?: number,
    patientAmountOutOfNetwork?: number,
    insuranceAmountInNetwork?: number,
    insuranceAmountOutOfNetwork?: number,
    estimationCreateDate?: NoTzDate,
    multipleCoverageCategoriesUsed?: string
}

export interface IApptTypeEstimationLinesInput {
    appointmentTypeId?: number,
    payerEntityId?: number,
    providerEntityId?: number,
    locationEntityId?: number
}

export interface IApptTypeEstimationLinesResponse {
    appointmentTypeId?: number,
    appointmentTypeName?: string,
    procedureCode?: string,
    procedureDesc?: string,
    negotiatedId?: number,
    modifiers?: string,
    icdCodes?: string,
    units?: number,
    chargeAmount?: number,
    allowedAmount?: number,
    chargeAmountExtended?: number,
    allowedAmountExtended?: number
}

export interface IEstimation {
    patientId: number,
    userId: string
    appointmentId: number,
    appointmentTypeId: number,
    payerEntityId: number,
    providerEntityId: number,
    serviceLocationEntityId: number,
    coverageCategoryServiceTypeCode: string,
    patientTransactionId: number,
    isManualEntry: boolean,
    isEstimateAccepted: boolean,
    chargeAmount: number,
    allowedAmount: number,
    patientAmountInNetworkEstimate: number,
    patientAmountOutOfNetworkEstimate: number,
    insuranceAmountInNetworkEstimate: number,
    insuranceAmountOutOfNetworkEstimate: number,
    estimationCreateDate: NoTzDate,
    copayAmountInNetwork: number,
    deductibleAmountIndInNetwork: number,
    deductibleAmountFamInNetwork: number,
    deductibleAmountRemainingIndInNetwork: number,
    deductibleAmountRemainingFamInNetwork: number,
    coinsurancePctInNetwork: number,
    outOfPocketAmountIndInNetwork: number,
    outOfPocketAmountFamInNetwork: number,
    outOfPocketAmountRemainingIndInNetwork: number,
    outOfPocketAmountRemainingFamInNetwork: number,
    copayAmountOutOfNetwork: number,
    deductibleAmountIndOutOfNetwork: number,
    deductibleAmountFamOutOfNetwork: number,
    deductibleAmountRemainingIndOutOfNetwork: number,
    deductibleAmountRemainingFamOutOfNetwork: number,
    coinsurancePctOutOfNetwork: number,
    outOfPocketAmountIndOutOfNetwork: number,
    outOfPocketAmountFamOutOfNetwork: number,
    outOfPocketAmountRemainingIndOutOfNetwork: number,
    outOfPocketAmountRemainingFamOutOfNetwork: number
}

export interface IEstimationPartial {
    patientId: number,
    appointmentTypeId?: number,
    dateOfService?: NoTzDate,
    payerEntityId?: number,
    providerEntityId?: number,
    serviceLocationEntityId?: number,
    isManualEntry: boolean,
    isEstimateAccepted: boolean,
    chargeAmount?: number,
    allowedAmount?: number,
    firstCoverageCategory?: string,
    multipleCoverageCategoriesUsed?: string | null,
    outputEstimationId?: number
}

export interface IEstimationLine {
    estimationId: number,
	procedureCode?: string,
	modifier?: string,
	diagnosisCode?: string,
	units?: number,
	chargeAmount?: number,
	allowedAmount?: number
}

export interface IEstimationLineResponse {
    estimationLineId: number,
    estimationId: number,
    dateOfService?: string,
	procedureCode?: string,
    procedureDesc?: string,
	modifier?: string,
	diagnosisCode?: string,
    diagnosisDesc?: string,
    isManualEntry: boolean, 
    isEstimateAccepted: boolean,
	units?: number,
	chargeAmount?: number,
	allowedAmount?: number,
    chargeAmountExtended?: number,
    allowedAmountExtended?: number
}

export interface IEstimationLineDeleteInputs {
    estimationLineId: number,
    estimationId: number
}

export interface IEstimationDeleteInputs {
    estimationId: number
}

export interface IEstimationGeneralResponse {
    isSuccess: boolean,
    message: string
}

export interface IEstimationLineUpdateInputs {
    estimationLineId: number,
    estimationId: number,
	procedureCode: string,
	modifier: string|undefined,
	diagnosisCode: string|undefined, 
	units: number,
	chargeAmount: number|undefined,
	allowedAmount: number|undefined
}

export interface IEstimationApptTypeCPTCountResponse {
    apptTypeCptCount: number
}

export interface IEstimationPatientPayerResponse {
    patientId?: number,
    patientEntityId?: number,
    insuredId?: number
    payerEntityId?: number,
    insuredIdCode?: string,
    insuredCoverage?: number,
    insuredCoverageDesc?: string,
    payerName?: string,
    payerId?: number,
    sourceOfPayCode?: string,
    sourceOfPayDesc?: string
}

export interface IEstimationFutureAppointmentResponse {
    appointmentId?: number,
    apptStartDate?: NoTzDate,
    providerEntityId?: number,
    providerName?: string,
    locationEntityId?: number,
    locationName?: string,
    appointmentTypeId?: number,
    appointmentTypeDesc?: string,
    sortOrder?: number
}

export interface IEstimationChargeAllowedInputs {
    procedureCode: string,
    payerEntityId: number,
    providerEntityId: number,
    locationEntityId: number
}

export interface IEstimationChargeAllowedAggregateInputs {
    baseInputs: IEstimationChargeAllowedInputs,
    daysToAggregate: number
}

export interface IEstimationChargeAllowedAggregateResponse {
    procedureCode: string,
    avgChargeCount: number,
    avgChargeAmount: number,
    avgAllowedCount: number,
    avgAllowedAmount: number,
    aggregateUsed: string
}

export interface IEstimationChargeAllowedFeeScheduleResponse {
    procedureCode: string,
    payerId?: number,
    billingProviderId?: number,
    serviceLocationId?: number,
    feeType?: string,
    chargeAmount: number,
    allowedAmount: number,
    negotiatedId?: number
}

export interface IEstimationEligServiceTypeResponse {
    serviceTypeCode?: string,
    serviceTypeDesc?: string
}

export interface IEstimationLastEligCheckResponse {
    patientTransactionId?: number,
    lastEligibilityDate?: NoTzDate,
    overallCoverage?: number
}

export interface IEstimationCopaymentInfo {
    copayFrequency?: string,
    copayAmount?: number
}

export interface IEstimationGetEstimateById {
    estimationId?: number,
    patientId?: number,
    patientEntityId?: number,
    userId?: string,
    appointmentTypeId?: number,
    dateOfService?: NoTzDate,
    payerEntityId?: number,
    providerEntityId?: number,
    serviceLocationEntityId?: number,
    coverageCategoryServiceTypeCode?: string,
    patientTransactionId?: number,
    isManualEntry: boolean,
    isEstimateAccepted: boolean,
    chargeAmount?: number,
    allowedAmount?: number,
    patientAmountInNetworkEstimate?: number,
    patientAmountOutOfNetworkEstimate?: number,
    insuranceAmountInNetworkEstimate?: number,
    insuranceAmountOutOfNetworkEstimate?: number,
    estimationCreateDate?: NoTzDate,
    copayAmountInNetwork?: number,
    deductibleAmountIndInNetwork?: number,
    deductibleAmountFamInNetwork?: number,
    deductibleAmountRemainingIndInNetwork?: number,
    deductibleAmountRemainingFamInNetwork?: number,
    coinsurancePctInNetwork?: number,
    outOfPocketAmountIndInNetwork?: number,
    outOfPocketAmountFamInNetwork?: number,
    outOfPocketAmountRemainingIndInNetwork?: number,
    outOfPocketAmountRemainingFamInNetwork?: number,
    copayAmountOutOfNetwork?: number,
    deductibleAmountIndOutOfNetwork?: number,
    deductibleAmountFamOutOfNetwork?: number,
    deductibleAmountRemainingIndOutOfNetwork?: number,
    deductibleAmountRemainingFamOutOfNetwork?: number,
    coinsurancePctOutOfNetwork?: number,
    outOfPocketAmountIndOutOfNetwork?: number,
    outOfPocketAmountFamOutOfNetwork?: number,
    outOfPocketAmountRemainingIndOutOfNetwork?: number,
    outOfPocketAmountRemainingFamOutOfNetwork?: number,
    copayDataInNetwork?: IEstimationCopaymentInfo[],
    copayDataOutOfNetwork?: IEstimationCopaymentInfo[],
    multipleCoverageCategoriesUsed?: string
}

export interface IEstimationSaveCalculationInputs {
    estimationId?: number,
    coverageCategoryServiceTypeCode?: string,
    patientTransactionId?: number,
    isEstimateAccepted: boolean,
    patientAmountInNetworkEstimate?: number,
    patientAmountOutOfNetworkEstimate?: number,
    insuranceAmountInNetworkEstimate?: number,
    insuranceAmountOutOfNetworkEstimate?: number,
    networkType?: NetworkTypes,
    totalChargeAmount?: number,
    totalAllowedAmount?: number
}

export interface IEstimationHistoryResponse {
    estimationId?: number,
    userId?: string,
    dateOfService?: NoTzDate,
    isEstimateAccepted: boolean,
    estimationCreateDate?: NoTzDate,    
    chargeAmount?: number,
    allowedAmount?: number,
    patientAmountInNetworkEstimate?: number,
    insuranceAmountInNetworkEstimate?: number,
    patientAmountOutOfNetworkEstimate?: number,
    insuranceAmountOutOfNetworkEstimate?: number,
    networkType?: NetworkTypes,
    patientElectronicSignatureDate?: Date
}

export interface IEstimationSaveToMmlInputs {
    estimationId?: number,
    pdfReportId?: number,
    patientId?: number
}

export interface IEstimationSaveToMmlResponse {
    mmlPatientCostEstimateId?: number,
    patientId?: number,
    estimationId?: number,
    pdfReportId?: number,
    userId?: string,
    estimationCreateDate?: NoTzDate,
    patientAmountInNetworkEstimate?: number,
    patientAmountOutOfNetworkEstimate?: number
}

export interface IEstimationAppointmentInfoResponse {
    appointmentId?: number,
    scheduledEntityId?: number,
    providerEntityId?: number,
    providerName?: string, 
    providerNpi?: string, 
    providerGroupName?: string, 
    providerIsRenderingProvider?: string, 
    providerActive?: number,
    serviceLocationId?: number,
    serviceLocationEntityId?: number,
    serviceLocationName?: string, 
    serviceLocationAddress?: string, 
    serviceLocationCity?: string, 
    serviceLocationState?: string, 
    serviceLocationZipCode?: string, 
    serviceLocationBillable?: boolean
}

export interface IEstimationPdfReportInfo {
    passed: boolean,
    reportKey?: string,
    reportUrl?: string,
    activeFax: boolean,
    directEmailAddress?: string
}

export interface IEstimationPdfReportKeyInfo {
    pdf_report_key_id: number,
    pdf_report_key: string,
    security_key: string,
    section: string,
    input?: string,
    general?: string,
    html?: string,
    layout: string,
    report_name?: string,
    pdf_report_id?: number,
    user_id: string,
    post_date: Date,
    pdf_login_key?: string,
    expiration_date?: Date,
    getdata_date?: Date,
    savepdf_date?: Date,
    getdata_count?: number
}

export interface IEstimationConvertToHmtlParams {
    input: string,
    pdfFileName: string,
    sectionName: string,
    htmlContent: string
}

export interface IEstimationMmlSignedEstimateData {
    estimationId?: number,
    signedEstimatePdfBinary?: Uint8Array,
    signedEstimatePdfBase64?: string,
    patientElectronicSignatureDate?: Date
}

export interface ISelectedEstimate {
    estimateId: number
    isAccepted: boolean
}

export interface IDefaultProviderSetup {
    providerEntityId: number
}

export interface IDefaultLocationSetup {
    locationEntityId: number
}

export interface IDefaultApptStatusSetup {
    apptStatusId: number[]
}

export interface IDefaultApptTypeSetup {
    apptTypeId: number[]
}

export interface IDefaultResourceSetup {
    resourceEntityId: number[]
}

export interface IEstimateAutoGenerateSetup {
    setupInfo: IAutoGenerateSetupInfo,
    appointmentStatusList: IAutoGenerateAppointmentStatus[],
    appointmentTypeList: IAutoGenerateAppointmentType[],
    resourceEntityList: IAutoGenerateResource[]
}

export interface IAutoGenerateSetupInfo {
    defaultProviderEntityId: number,
    defaultServiceLocationEntityId: number
}

export interface IAutoGenerateAppointmentStatus {
    appointmentStatusId: number
}

export interface IAutoGenerateAppointmentType {
    appointmentTypeId: number
}

export interface IAutoGenerateResource {
    resourceEntityId: number
}

export interface IAutoGenerateLogResults {
    logId: number,
    logDate: Date,
    appointmentId: number,
    appointmentDate: Date,
    patientId: number,
    isEstimateCreated: boolean,
    estimateId?: number,
    logDescription: string,
    appointmentCreateDate: Date
}

export enum CompanySettingAllowedMethod {
    PaymentAggregate = "0",
    FeeSchedule = "1"
}

export enum CompanySettingEstimationLastEligCheck {
    CurrentMonth = "0",
    Last15Days = "1",
    CurrentDate = "2"
}

export enum PayerSourceOfPay {
    SelfPay = "A",
    Commercial = "F"
}

export enum NetworkTypes {
    In = "in",
    Out = "out"
}

